import { Card, Table, type TableColumn } from '../../../ui';
import OveroptBadge from './OveroptBadge';
import { fmtDec, fmtInt, fmtWhen, overoptOf, type OveroptPhrase, type PageRow } from './format';

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
const flatTable: React.CSSProperties = { background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', minWidth: '680px' };

const SOURCE_LABEL: Record<string, string> = { query: 'search', url: 'URL', h1: 'H1', headings: 'headings', ahrefs: 'Ahrefs' };


/**
 * Page detail → Keyword density: the phrases the screen (scripts/lib/overopt.mjs) counted on this page, with
 * the numbers behind the flag. The fix itself is queued from the journal below.
 */
export default function OveroptCard({ page }: { page: PageRow }) {
  const result = overoptOf(page);
  const totalHeadings = result?.headings ?? 0;

  const columns: TableColumn<OveroptPhrase>[] = [
    {
      key: 'phrase',
      label: 'Phrase',
      width: '30%',
      render: (_v, row) => (
        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`Found from: ${row.sources.map((s) => SOURCE_LABEL[s] ?? s).join(', ')}`}>
          {row.target ? <span style={{ color: 'var(--accent-text)', fontSize: 'var(--text-2xs)' }}>TARGET </span> : null}
          {row.phrase}
          <span style={{ ...faint, fontSize: 'var(--text-2xs)' }}> · {row.sources.map((s) => SOURCE_LABEL[s] ?? s).join(', ')}</span>
        </span>
      ),
    },
    { key: 'uses', label: 'Uses', align: 'right', mono: true, render: (v) => fmtInt(v as number) },
    { key: 'density', label: 'Density', align: 'right', mono: true, render: (v) => `${fmtDec(v as number, 2)}%` },
    {
      key: 'headings',
      label: 'Headings',
      align: 'right',
      mono: true,
      render: (v) => (
        <span title="H1–H3 that hold every word of the phrase, in any order">
          {fmtInt(v as number)}
          <span style={faint}> / {fmtInt(totalHeadings)}</span>
        </span>
      ),
    },
    {
      key: 'in_url',
      label: 'In',
      render: (_v, row) => {
        const where = [row.in_url && 'URL', row.in_title && 'title', row.in_h1 && 'H1'].filter(Boolean).join(' · ');
        return where || <span style={faint}>—</span>;
      },
    },
    {
      key: 'position',
      label: 'Search',
      align: 'right',
      mono: true,
      render: (_v, row) =>
        row.position == null ? (
          <span style={faint}>—</span>
        ) : (
          <span>
            pos {fmtDec(row.position)} <span style={faint}>· {fmtInt(row.impressions)} impr</span>
          </span>
        ),
    },
    { key: 'level', label: '', render: (v) => <OveroptBadge level={v as OveroptPhrase['level']} /> },
  ];

  const note = (text: React.ReactNode) => <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{text}</div>;

  return (
    <Card
      eyebrow="Keyword density"
      title={
        !result ? 'Not checked yet' : result.error ? 'Couldn’t read the page' : result.level === 'over' ? 'Over-optimized' : result.level === 'under' ? 'Under-optimized' : 'In line'
      }
      actions={<OveroptBadge level={result?.level ?? null} />}
    >
      {!result ? (
        note('The check runs with the next pages sync.')
      ) : result.error ? (
        note(`The check couldn’t fetch it (${result.error}), so there are no counts.`)
      ) : !result.phrases.length ? (
        note('No search phrase on this page to count.')
      ) : (
        <div style={{ overflowX: 'auto', margin: '0 calc(-1 * var(--space-2))' }}>
          <Table columns={columns} rows={result.phrases} dense style={flatTable} />
        </div>
      )}
      <div className="t-num" style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', ...faint }}>
        {result && !result.error ? `${fmtInt(result.words)} words, ${fmtInt(result.headings)} H1–H3 headings, nav and footer left out · ` : ''}
        In line = 0.5% to 1% of the words. Over-optimized = any phrase above 1%; under-optimized = the target below 0.5%.
        {result?.fetched_from ? ` · read from ${new URL(result.fetched_from).host}, the live domain blocks bots` : ''}
        {page.overopt_checked_at ? ` · checked ${fmtWhen(page.overopt_checked_at)}` : ''}
      </div>
    </Card>
  );
}
